// cleanSlate JS entry.
//
// Bridges to the base tier's hiveMind (the framework orchestrator) and injects
// cleanSlate's own behaviour adapters, mirroring the bootstrap5/metronic_8
// entry pattern but in ESM (Vite). The base scripts are CommonJS; Vite's
// @rollup/plugin-commonjs (configured in tools/vite.config.js to include
// core/themes/base/scripts) synthesises the default exports imported below.
//
// cleanSlate has no app override yet, so it reuses base's app.js, and resolves
// component JS from the base, neutral and cleanSlate tiers (see the
// requireComponent injection below). requireController stays at its base
// default (returns false) — cleanSlate has no page-controller JS yet.

import hiveMind from '../../../../core/themes/base/scripts/hiveMind.js';
import behaviors from './behaviors.js';
import app from '../../../../core/themes/base/scripts/app.js';
import initSidebar from './sidebar.js';
import initTopnav from './topnav.js';
import initTabs from './tabs.js';
import initCollapse from './collapse.js';
import initMenu from './menu.js';
import * as Draggable from '@shopify/draggable';

// Expose the Shopify Draggable library as the global the base component JS expects:
// base avCols.js (column picker) and itemsList.js / editList.js (row drag-sort) read
// `Draggable.Sortable` at call time, and skip drag-sorting when the global is absent.
// The ESM build's named exports (Sortable, Draggable, …) match the UMD global's shape.
window.Draggable = Draggable;

// Replace base's throwing behaviour-contract stubs with cleanSlate's adapters.
hiveMind.behaviors = behaviors;

// Reuse the base app (no theme-specific page init needed yet).
hiveMind.app = app;

// Bundle + resolve the base AND neutral component JS.
//
// hiveMind.newComponent() normally resolves a component module via a *dynamic*
// require('../components/' + type + '/' + type). Webpack (metronic_8/bootstrap5)
// compiles that into a bundled require-context; Rollup/Vite cannot statically
// analyse a dynamic require, so left alone the component JS is never bundled
// (and the dynamic require hits @rollup/plugin-commonjs's throwing stub at
// runtime, swallowed by newComponent's try/catch). We therefore bundle the
// component modules ourselves with import.meta.glob({ eager: true }) — which
// emits a static import per match — and inject a resolver so newComponent's
// step 1 (requireComponent) returns them before the dynamic fallback ever runs.
//
// Three tiers are bundled, lowest precedence first: base, then the app-level
// neutral tier (app/themes/neutral/components — theme-agnostic, app-specific
// components shared by every leaf theme), then cleanSlate's own leaf component
// JS (components/<name>/<name>.js, written as ESM and importing the base module
// they extend). Later sets win, matching the leaf -> neutral -> base order.
const componentModuleSets = [
    import.meta.glob('../../../../core/themes/base/components/*/*.js', { eager: true }), // base (lowest)
    import.meta.glob('../../neutral/components/*/*.js', { eager: true }),                // neutral (overrides base)
    import.meta.glob('../components/*/*.js', { eager: true })                            // cleanSlate leaf (overrides both)
];

// Map componentType -> component module object. Only the canonical entry whose
// file name matches its directory (e.g. input/input.js) is the component
// module; any helper .js in the same dir is bundled but not registered. Later
// module sets overwrite earlier ones, so leaf wins over neutral wins over base.
const components = {};
for (const moduleSet of componentModuleSets) {
    for (const modulePath in moduleSet) {
        const match = modulePath.match(/\/components\/([^/]+)\/([^/]+)\.js$/);
        if (match && match[1] === match[2]) {
            const mod = moduleSet[modulePath];
            // commonjs-transformed modules expose module.exports as `default`.
            components[match[1]] = (mod && mod.default !== undefined) ? mod.default : mod;
        }
    }
}

// Static resolver — returns the resolved component object for a type, or false
// so hiveMind falls through to its generic 'component' base for types without
// their own JS (hidden, checkbox, …).
hiveMind.requireComponent = function (componentType) {
    return Object.prototype.hasOwnProperty.call(components, componentType)
        ? components[componentType]
        : false;
};

// Bootstrap once the DOM is ready: start hiveMind, wire the layout chrome and the
// product-page tab strip (a no-op on pages without .zit-cs-tabs). The two layouts are
// mutually exclusive (menuPosition), so exactly one of initSidebar / initTopnav finds
// its markup and the other is a no-op. For collapsible windows/components: seed each
// [data-zit-collapsible] region's initial state classes (behaviors.collapse.createInstances),
// then register the click delegate that toggles them (initCollapse — cleanSlate's
// frameworkless collapse handler). Both are no-ops on pages without collapsibles.
// initMenu registers the click delegate that opens actionsGroup overflow menus.
function boot() {
    hiveMind.main();
    initSidebar();
    initTopnav();
    initTabs();
    hiveMind.behaviors.collapse.createInstances();
    initCollapse(hiveMind);
    initMenu(hiveMind);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
